import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { HTTPError } from "ky"

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@f2e/ui/components/base"
import { InAppBrowserWarning } from "@f2e/ui/components/Auth"

import { useToast, useSysConfig } from "@/hooks"
import { useLoginApi, LoginApiRequest } from "@/hooks/api/account"
import { API_PREFIX } from "@/constants"
import { ErrorCode } from "@/constants/errors"

const LoginForm = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const config = useSysConfig()
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const {
    error: loginError,
    data: loginRespData,
    fetchFn: login,
  } = useLoginApi()

  // error handling
  useEffect(() => {
    if (!loginError || !(loginError instanceof HTTPError)) return
    console.error("Login Error", loginError)

    if (loginError.response.status === 403) {
      // maybe already logged in
      navigate("/")
    }
    if (!loginRespData) return

    let errorMsg = loginRespData.message
    if (loginRespData.error_code === ErrorCode.AUTH_INVALID_CREDENTIALS) {
      errorMsg = t("common.auth.login.errors.invalidCredentials")
    } else if (loginRespData.error_code === ErrorCode.AUTH_EMAIL_NOT_VERIFIED) {
      errorMsg = t("common.auth.login.errors.emailNotVerified")
    } else if (loginRespData.error_code === ErrorCode.SYS_VALIDATION_ERROR) {
      const regex = /'msg':\s*'([^']+)'/
      const match = regex.exec(loginRespData.message)
      if (match && match[1]) {
        errorMsg = match[1]
      }
    }

    alertToast({
      title: t("common.auth.login.errors.title"),
      description: errorMsg,
    })
  }, [loginError, loginRespData, navigate, alertToast, t])

  const onLogin = () => {
    if (!email || !password) {
      const errorMsg = t("common.auth.login.errors.emailPasswordRequired")
      console.log(errorMsg)
      alertToast({
        title: t("common.auth.login.errors.title"),
        description: errorMsg,
      })
      return
    }

    const payload: LoginApiRequest = { email, password }
    login({
      jsonBody: payload,
      callbackFn: (resp) => {
        if (!resp || !resp.data) return

        console.log("Login!")
        localStorage.setItem("loggedIn", JSON.stringify({ type: "email" }))

        const redirectAfterLogin = localStorage.getItem("redirectAfterLogin")
        if (redirectAfterLogin) {
          console.debug("do redirect after login", redirectAfterLogin)
          localStorage.removeItem("redirectAfterLogin")
          navigate(redirectAfterLogin)
          return
        }

        navigate("/")
      },
    })
  }

  const onGoogleAuth = () => {
    console.log("Google Login", config)
    localStorage.setItem("loggedIn", JSON.stringify({ type: "google" }))
    const authUrl = `${config.serverRoot}${API_PREFIX}/accounts/google/login`
    window.location.href = authUrl
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onLogin()
    }
  }

  return (
    <Card className="mx-auto w-full max-w-sm">
      <CardHeader>
        <CardTitle className="text-2xl text-center">
          {t("common.auth.login.title")}
        </CardTitle>
        <CardDescription>{t("common.auth.login.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">{t("common.auth.fields.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKeyDown}
              required
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="password">
                {t("common.auth.fields.password")}
              </Label>
              <button
                type="button"
                className="ml-auto inline-block text-sm underline"
                onClick={() => navigate("/forgot-password")}
              >
                {t("common.auth.login.forgotPassword")}
              </button>
            </div>
            <Input
              id="password"
              type="password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onKeyDown}
              required
            />
          </div>
          <Button type="submit" className="w-full" onClick={onLogin}>
            {t("common.auth.login.submit")}
          </Button>
          <InAppBrowserWarning />
          <Button variant="outline" className="w-full" onClick={onGoogleAuth}>
            {t("common.auth.login.google")}
          </Button>
        </div>
        <div className="mt-4 text-center text-sm">
          {t("common.auth.login.noAccount")}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => navigate("/signup")}
          >
            {t("common.auth.login.signup")}
          </button>
        </div>
      </CardContent>
    </Card>
  )
}

export default LoginForm
